import { z } from 'zod';

import { BooleanStringSchema, DateStringSchema, LocalDateTimeStringSchema, TimeStringSchema } from '../../common';
import { ExperienceKind, ExperienceVersion } from '../../experience/const';
import { buildQueryString } from '../../utils/querystring';
import { BookingMode } from '../booking/booking';
import { BaseParticipantSchema, PurchaserSchema } from '../booking/participants';
import { FiltersSchema, makeEndpoint, HostSchema, paginateQuerySchema, paginateResponseSchema } from '../common';
import { PriceTierSchema } from '../experience/price-tier-rules';
import { LocationSchema } from '../location/location';
import { ExperienceSchema } from '../resource/common';
import { ResourceGroupResponseSchema } from '../resource/resource-group';
import { ResourceGroupCollectionResponseSchema, ResourceGroupCollectionSchema } from '../resource/resource-group-collection';

export enum SessionStatus {
  OPEN = 'OPEN',
  OVERLAPPED = 'OVERLAPPED',
  CONFIRMED = 'CONFIRMED',
  WAITLISTED = 'WAITLISTED',
  OVERLAPPED_BY_WAITLIST = 'OVERLAPPED_BY_WAITLIST',
}

/** Why a session with seats left can't be booked. */
export enum SessionUnavailableReason {
  /** The pool unit this resource sells is out of service, blacked out, or held through another group. */
  RESOURCE_UNAVAILABLE = 'resource_unavailable',
}

// Server-side session filter vocabulary for the sessions endpoints (`status` query param).
// Distinct from SessionStatus: these are filter predicates over the final computed session,
// not raw statuses. Grows as consumers need more values (e.g. confirmed, blocked) — each new
// value must define its predicate and, with maxSessionsPerDay, its cap-ranking behavior.
// `canceled` is deliberately absent: cancelled events never appear in the response (overlap-
// cancelled ones surface as SessionStatus.OVERLAPPED), so it could only ever match nothing.
export enum SessionStatusFilter {
  AVAILABLE = 'available',
}

export const GetSessionsQuerySchema = z.object({
  experienceId: z.string().uuid().optional(),
  resourceGroupCollectionId: z.string().uuid().optional(),
  resourceGroupId: z.string().uuid().optional(),
  filters: FiltersSchema.optional(),
  mode: z.nativeEnum(BookingMode).optional(),
  version: z.nativeEnum(ExperienceVersion).optional(),
  hostId: z.string().uuid().optional(),
  q: z.string().optional(),
  includesAllConfirmedEvents: BooleanStringSchema.optional(),
  includePendingEvents: BooleanStringSchema.optional(),
  bookingId: z.string().uuid().optional(),
  // Caps the number of sessions returned per calendar day (by local `startDateTime` date).
  // The monthly view sends `displayLimit + 1` so it can show `displayLimit` and detect overflow.
  maxSessionsPerDay: z.coerce.number().int().positive().optional(),
  // Server-side session filter. `available` keeps only sessions with no participants and
  // not overlap-cancelled (the guest embed's availability predicate) and, combined with
  // `maxSessionsPerDay`, ranks bookable rows ahead of dead ones inside the per-day cap — so a
  // day whose earliest slots are all booked/blocked still surfaces its later open ones.
  status: z.nativeEnum(SessionStatusFilter).optional(),
});

export const SessionBookingParticipantSchema = BaseParticipantSchema.extend({
  id: z.string(),
  cancelledAt: z.string().datetime().nullable(),
  ticketNumber: z.string().nullable(),
  isCheckedIn: z.boolean(),
  priceTierId: z.string().optional(),
});
export const SessionBookingParticipantsSchema = z.array(SessionBookingParticipantSchema);
export type SessionBookingParticipants = z.infer<typeof SessionBookingParticipantsSchema>;

export const SessionBookingsSchema = z.array(
  z.object({
    id: z.string(),
    mode: z.nativeEnum(BookingMode),
    purchaser: PurchaserSchema,
    participants: SessionBookingParticipantsSchema,
  }),
);

export const GetSessionsResponseSchema = z.object({
  sessions: z.array(
    z.object({
      sessionId: z.string().uuid().nullable(),
      scheduleId: z.string().uuid().nullable(),
      isRepeating: z.boolean(),
      isPrivateEvent: z.boolean(),
      priceTiers: z.array(PriceTierSchema),
      startDateTime: LocalDateTimeStringSchema,
      sessionDuration: z.number().int().positive(),
      purchaserName: z.string().optional(),
      participantCount: z.number().int().nonnegative(),
      isFullyCheckedIn: z.boolean(),
      capacity: z.number().nullable(), // open experiences do not have a capacity
      experienceId: z.string().uuid(),
      status: z.nativeEnum(SessionStatus),
      hostedBy: HostSchema.nullable(),
      isBookableViaNegativeCutOffHours: z.boolean(),
      isPromotedSession: z.boolean(),
      isContinuousAvailability: z.boolean().optional(),
      isBookableAvailability: z.boolean().optional(),
      unavailableReason: z.nativeEnum(SessionUnavailableReason).optional(),
    }),
  ),
  experiences: z.array(ExperienceSchema),
  resourceGroupCollections: z.array(
    ResourceGroupCollectionResponseSchema.extend({
      resourceGroups: z.array(ResourceGroupResponseSchema),
    }),
  ),
});

export const GetSessionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    from,
    to,
    query,
  }: {
    brandId: string;
    from: string;
    to: string;
    query: z.infer<typeof GetSessionsQuerySchema>;
  }) => `/v2/brands/${brandId}/sessions/${from}/${to}?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: GetSessionsResponseSchema,
  querySchema: GetSessionsQuerySchema,
});

export const GetMineSessionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    from,
    to,
    query,
  }: {
    brandId: string;
    from: string;
    to: string;
    query: z.infer<typeof GetSessionsQuerySchema>;
  }) => `/v2/brands/${brandId}/sessions/mine/${from}/${to}?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: GetSessionsResponseSchema,
  querySchema: GetSessionsQuerySchema,
});

const UpdateSessionRequestSchema = z.object({
  eventCapacity: z
    .array(
      z.object({
        startDateTime: LocalDateTimeStringSchema,
        // should not be optional once the dashboard has been updated
        sessionDuration: z.number().int().positive().optional(),
        maxQuantity: z.number().int().nonnegative(),
      }),
    )
    .min(1),
});

export const UpdateSessionEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ scheduleId }: { scheduleId: string }) => `/v1/schedule/${scheduleId}/update-session`,
  requestSchema: UpdateSessionRequestSchema,
  responseSchema: z.void(),
});

export const RemoveManySessionsRequestSchema = z.object({
  from: DateStringSchema,
  to: DateStringSchema.optional(),
  sessions: z.array(
    z.object({
      experienceId: z.string().uuid(),
      duration: z.number(),
      startTime: TimeStringSchema,
    }),
  ),
});

export const RemoveManySessionsEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `/v1/brands/${brandId}/remove-many-sessions`,
  requestSchema: RemoveManySessionsRequestSchema,
  responseSchema: z.void(),
});

export const RemoveSessionRequestSchema = z.object({
  startTimes: z.array(LocalDateTimeStringSchema).min(1).max(500),
  scheduleIds: z.array(z.string().uuid()).optional(),
  from: DateStringSchema.optional(),
  to: DateStringSchema.optional(),
  sessionDuration: z.number().optional(),
});

export const RemoveSessionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ experienceId }: { experienceId: string }) => `/v1/experiences/${experienceId}/remove-sessions`,
  requestSchema: RemoveSessionRequestSchema,
  responseSchema: z.void(),
});

export const GetConfirmedSessionsQuerySchema = paginateQuerySchema()
  .omit({ filters: true, sortDir: true, sortBy: true })
  .extend({
    startDate: DateStringSchema.optional(),
    endDate: DateStringSchema.optional(),
    experienceId: z.string().uuid().optional(),
    resourceGroupCollectionId: z.string().uuid().optional(),
    hostId: z.string().uuid().optional(),
    brandId: z.string().uuid().optional(),
  });

export const ConfirmedSessionsCollectionsSchema = ResourceGroupCollectionSchema.pick({
  id: true,
  name: true,
  kind: true,
  eventType: true,
});

export const ConfirmedSessionSchema = z.object({
  eventId: z.string().uuid(),
  startDateTime: LocalDateTimeStringSchema,
  sessionDuration: z.number(),
  participantsCount: z.number(),
  isPrivate: z.boolean(),
  experience: z.object({
    id: z.string().uuid(),
    title: z.string(),
    hostName: z.string().nullable(),
    location: LocationSchema,
    maxParticipantsCount: z.number(),
    isBookable: z.boolean(),
    resourceGroupId: z.string().uuid().nullable(),
    kind: z.nativeEnum(ExperienceKind),
    resourceGroupCollectionId: z.string().uuid().nullable(),
  }),
  collection: ConfirmedSessionsCollectionsSchema.nullable(),
  eventCapacity: z.object({ maxQuantity: z.number() }).nullable(),
});

const GetConfirmedSessionsResponseSchema = paginateResponseSchema(ConfirmedSessionSchema);

export const GetConfirmedSessionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetConfirmedSessionsQuerySchema> }) =>
    `/v1/brands/${brandId}/confirmed-sessions?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: GetConfirmedSessionsResponseSchema,
  querySchema: GetConfirmedSessionsQuerySchema,
});

export const GetMineConfirmedSessionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetConfirmedSessionsQuerySchema> }) =>
    `/v1/brands/${brandId}/confirmed-sessions/mine?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: GetConfirmedSessionsResponseSchema,
  querySchema: GetConfirmedSessionsQuerySchema,
});
